/**
 * Paie mensuelle (données du serveur) : personnel au salaire fixe, enseignants heures × tarif horaire.
 * Chacun n'est payé qu'une fois par mois. Un paiement écrit les dépenses « Salaires » de chaque cycle ; il s'annule (motif).
 */
import { http, orUndefined } from "./http";

export type PayrollPersonType = "ENSEIGNANT" | "PERSONNEL";

export interface PayrollLine {
  personType: PayrollPersonType;
  personId: string;
  personName: string;
  cycleId: string;
  hours: number | null;
  hourlyRate: number | null;
  amount: number;
  /** Aperçu : paiement qui a déjà payé cette personne ce mois-ci (null : pas encore payée). */
  paidInRunId?: string | null;
}

export interface PayrollPreview {
  month: string;
  lines: PayrollLine[];
  /** Reste à payer pour le mois. */
  total: number;
  /** Déjà versé pour le mois. */
  paidTotal: number;
}

export interface PayrollRun {
  id: string;
  month: string;
  paidOn: string;
  total: number;
  recordedBy: string;
  cancelledAt: string | null;
  cancelReason: string;
  cancelledBy: string;
  lines: PayrollLine[];
}

interface ApiLine extends Omit<PayrollLine, "personId" | "cycleId" | "paidInRunId"> {
  personId: number;
  cycleId: number;
  paidInRunId?: number | null;
}

interface ApiRun {
  id: number;
  month: string;
  paidOn: string;
  total: number;
  recordedByName: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  cancelledByName: string | null;
  lines: ApiLine[];
}

const toLine = ({ paidInRunId, ...l }: ApiLine): PayrollLine => ({
  ...l,
  personId: String(l.personId),
  cycleId: String(l.cycleId),
  ...(paidInRunId === undefined ? {} : { paidInRunId: paidInRunId === null ? null : String(paidInRunId) }),
});

function toRun(r: ApiRun): PayrollRun {
  return {
    id: String(r.id),
    month: r.month,
    paidOn: r.paidOn,
    total: r.total,
    recordedBy: r.recordedByName,
    cancelledAt: r.cancelledAt,
    cancelReason: r.cancelReason ?? "",
    cancelledBy: r.cancelledByName ?? "",
    lines: r.lines.map(toLine),
  };
}

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

/** « 2026-10 » -> « octobre 2026 ». */
export function monthLabel(month: string): string {
  const [year, m] = month.split("-").map(Number);
  return `${MONTHS[m - 1] ?? "?"} ${year}`;
}

export async function getPayrollPreview(month: string): Promise<PayrollPreview> {
  const res = await http.get<{ month: string; lines: ApiLine[]; total: number; paidTotal: number }>(
    "/payroll/preview",
    { month },
  );
  return { month: res.month, lines: res.lines.map(toLine), total: res.total, paidTotal: res.paidTotal };
}

export async function getPayrollRuns(): Promise<PayrollRun[]> {
  return (await http.get<ApiRun[]>("/payroll")).map(toRun);
}

/**
 * Paie le mois pour les personnes données : toutes celles pas encore payées (« Valider la paie ») ou une
 * seule (heures des enseignants et montants du personnel tels que saisis).
 */
export async function validatePayroll(month: string, paidOn: string, lines: PayrollLine[]): Promise<PayrollRun> {
  return toRun(
    await http.post<ApiRun>("/payroll", {
      month,
      paidOn: orUndefined(paidOn),
      lines: lines.map((l) =>
        l.personType === "ENSEIGNANT" && l.hours !== null
          ? { personType: l.personType, personId: Number(l.personId), hours: l.hours }
          : { personType: l.personType, personId: Number(l.personId), amount: l.amount },
      ),
    }),
  );
}

export async function cancelPayroll(id: string, reason: string): Promise<PayrollRun> {
  return toRun(await http.post<ApiRun>(`/payroll/${id}/cancel`, { reason: reason.trim() }));
}
